import { emptyApi } from "./emptyApi";
import {
  PlatformTourPreference,
  Profile,
  User,
  UserPreferenceKey,
  UserPreferences,
} from "types/Models/user";

export const profileApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getUserProfile: build.query<Profile, void>({
      query: () => {
        return {
          url: `/api/inkle/users/profile/`,
        };
      },
      providesTags: (result) => [{ type: "PROFILE" }],
    }),
    getUserEtdCount: build.query<{ task_etd_pending_count: number }, void>({
      query: () => {
        return {
          url: `/api/inkle/users/pending_etd_count/`,
        };
      },
    }),
    updateWhatsappConsent: build.mutation<
      Profile,
      {
        payload: { whatsapp_consent: boolean };
      }
    >({
      query: ({ payload }) => {
        return {
          url: "/api/inkle/users/profile/whatsapp-consent/",
          method: "PATCH",
          body: payload,
        };
      },
      invalidatesTags: [{ type: "PROFILE" }],
    }),
    updateAdditionalInfo: build.mutation<
      User,
      {
        linkedIn_url?: string;
        calendar_url?: string;
        secondary_mobile?: string;
        current_timezone?: string;
        slack_mention_id?: string;
      }
    >({
      query: (body) => ({
        url: `/api/inkle/users/profile/`,
        method: "PUT",
        body: { operation: "ADDITIONAL_INFO_UPDATE", ...body },
      }),
      invalidatesTags: [{ type: "PROFILE" }],
    }),
    setCallbackGmail: build.query<
      { callback_url: string; status: string },
      {
        error: string;
        code: string;
        source?: string | null;
        entity_id?: string | null;
      }
    >({
      query: ({ error, code, entity_id, source }) => ({
        url: `/api/inkle/users/auth/email/callback/`,
        method: "POST",
        body: {
          code,
          error,
          state: {
            entity_id,
            source,
          },
        },
      }),
    }),
    updateUserPreference: build.mutation<
      { preferences: UserPreferences },
      { key: UserPreferenceKey; value: Partial<PlatformTourPreference> }
    >({
      query: (body) => ({
        url: "/api/inkle/users/profile/preferences/",
        method: "PATCH",
        body,
      }),
      // Apply the change to the cached profile right away so the tour UI reacts instantly
      async onQueryStarted({ key, value }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          profileApi.util.updateQueryData(
            "getUserProfile",
            undefined,
            (draft) => {
              const current = draft.preferences?.[key];
              if (!current) return;
              draft.preferences = {
                ...draft.preferences,
                [key]: { ...current, ...value },
              };
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [{ type: "PROFILE" }],
    }),
  }),
});

export const {
  useGetUserProfileQuery,
  useLazyGetUserProfileQuery,
  useGetUserEtdCountQuery,
  useSetCallbackGmailQuery,
  useUpdateAdditionalInfoMutation,
  useUpdateWhatsappConsentMutation,
  useUpdateUserPreferenceMutation,
} = profileApi;
